/**
 * Spend Rules Grid Skeleton Pattern
 *
 * Skeleton loaders for the spend rules list block.
 * Shows placeholder cards while data is loading.
 */
import type { FC } from 'react';
import { Skeleton } from '../index';

export interface SpendRulesGridSkeletonProps {
	/**
	 * Number of columns to display
	 * @default 3
	 */
	columns?: number;

	/**
	 * Optional aria message for screen readers
	 */
	ariaMessage?: string;
}

/**
 * Spend rules grid skeleton component
 *
 * Renders a grid of skeleton cards matching the spend rules layout.
 */
export const SpendRulesGridSkeleton: FC<SpendRulesGridSkeletonProps> = ({
	columns = 3,
	ariaMessage = 'Loading rewards...',
}) => (
	<div
		className="leat-spend-rules-list__grid"
		style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
		aria-live="polite"
		aria-label={ariaMessage}
	>
		{Array.from({ length: columns }).map((_, i) => (
			<SpendRuleCardSkeleton key={i} />
		))}
	</div>
);

/**
 * Individual spend rule card skeleton
 */
const SpendRuleCardSkeleton: FC = () => (
	<div className="leat-item leat-item--skeleton">
		{/* Badge skeleton in top right */}
		<div style={{ position: 'absolute', top: '12px', right: '12px' }}>
			<Skeleton width="80px" height="24px" borderRadius="12px" />
		</div>

		<div className="leat-item__content">
			{/* Image skeleton */}
			<div style={{ width: '100%', height: '200px', marginBottom: '0.25rem' }}>
				<Skeleton width="100%" height="100%" borderRadius="6px" />
			</div>

			{/* Label skeleton */}
			<div style={{ marginBottom: '8px', textAlign: 'center' }}>
				<Skeleton width="80%" height="20px" style={{ margin: '0 auto' }} />
			</div>

			{/* Description skeleton (optional) */}
			<div style={{ marginBottom: '8px', textAlign: 'center' }}>
				<Skeleton width="90%" height="14px" style={{ margin: '0 auto 4px' }} />
				<Skeleton width="70%" height="14px" style={{ margin: '0 auto' }} />
			</div>

			{/* Progress bar skeleton */}
			<div style={{ width: '100%', marginTop: '16px', padding: '12px 0' }}>
				<Skeleton width="100%" height="8px" borderRadius="4px" style={{ marginBottom: '8px' }} />
				<Skeleton width="60%" height="13px" style={{ margin: '0 auto' }} />
			</div>
		</div>

		{/* Action button skeleton */}
		<div className="leat-item__actions" style={{ marginTop: '1rem' }}>
			<Skeleton width="100%" height="40px" borderRadius="4px" />
		</div>
	</div>
);
